import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';

import { PageHeader } from '@opengov/components-page-header';
import { Magnify, Pencil, Plus, TrashCan } from '@opengov/react-capital-assets';

import { ResourceFormDialog, type ResourceFormValues } from '../components/vcm/ResourceFormDialog';
import { SectionCard } from '../components/vcm/SectionCard';
import { RESOURCE_CATEGORIES, RESOURCE_STATUSES } from '../lib/vcm/constants';
/* MOCK DATA — replace with real /v2 API calls when backend is connected */
import { mockGetResources } from '../mocks/vcm-mock-data';
import type { TcrResource } from '../types/vcm';

type StatusChipColor = 'success' | 'error' | 'warning' | 'default';

const STATUS_COLOR: Record<string, StatusChipColor> = {
  Active: 'success',
  Inactive: 'error',
  'Needs Review': 'warning',
  'Under Review': 'warning',
};

const ALL_COUNTIES = 'All counties';
const ALL_CATEGORIES = 'All categories';
const ALL_STATUSES = 'All statuses';
const ALL_GEO = 'All geographic areas';

export function ResourceManagePage() {
  /* MOCK DATA — editable in-session (Add/Edit/Delete) until /v2 is wired. */
  const [resources, setResources] = useState<TcrResource[]>(() => mockGetResources());

  const [query, setQuery] = useState('');
  const [countyFilter, setCountyFilter] = useState(ALL_COUNTIES);
  const [categoryFilter, setCategoryFilter] = useState(ALL_CATEGORIES);
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES);
  const [geoFilter, setGeoFilter] = useState(ALL_GEO);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<TcrResource | null>(null);
  const [pendingDelete, setPendingDelete] = useState<TcrResource | null>(null);

  const counties = useMemo(
    () => [ALL_COUNTIES, ...new Set(resources.map((r) => r.county))],
    [resources],
  );
  const geographicAreas = useMemo(
    () => [
      ALL_GEO,
      ...new Set(resources.map((r) => r.geographicArea).filter((g): g is string => Boolean(g))),
    ],
    [resources],
  );

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    let results: TcrResource[] = resources;
    if (q) {
      results = results.filter((r) =>
        `${r.name} ${r.category} ${r.county} ${r.organization}`.toLowerCase().includes(q),
      );
    }
    if (countyFilter !== ALL_COUNTIES) {
      results = results.filter((r) => r.county === countyFilter);
    }
    if (categoryFilter !== ALL_CATEGORIES) {
      results = results.filter((r) => r.category === categoryFilter);
    }
    if (statusFilter !== ALL_STATUSES) {
      results = results.filter((r) => r.status === statusFilter);
    }
    if (geoFilter !== ALL_GEO) {
      results = results.filter((r) => r.geographicArea === geoFilter);
    }
    return results;
  }, [resources, query, countyFilter, categoryFilter, statusFilter, geoFilter]);

  const openAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (resource: TcrResource) => {
    setEditing(resource);
    setDialogOpen(true);
  };
  const confirmDelete = () => {
    if (pendingDelete) {
      setResources((prev) => prev.filter((r) => r.id !== pendingDelete.id));
    }
    setPendingDelete(null);
  };
  const handleSave = (values: ResourceFormValues) => {
    const now = new Date().toISOString();
    setResources((prev) => {
      if (editing) {
        return prev.map((r) => (r.id === editing.id ? { ...r, ...values, updatedAt: now } : r));
      }
      return [{ id: `res-${Date.now()}`, ...values, createdAt: now, updatedAt: now }, ...prev];
    });
    setDialogOpen(false);
  };

  return (
    <Box
      sx={{
        p: (theme) => theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: (theme) => theme.spacing(2.5),
      }}
    >
      <PageHeader
        title="Manage Resources"
        description="Create and edit resource records available to survivors."
        actions={[
          <Button key="new" variant="contained" startIcon={<Plus />} onClick={openAdd}>
            New Resource
          </Button>,
        ]}
      />

      <SectionCard title={`Resources (${filtered.length})`} disableContentPadding>
        <Box sx={{ p: (theme) => theme.spacing(2) }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                placeholder="Search resources…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                size="small"
                fullWidth
                slotProps={{
                  input: {
                    startAdornment: (
                      <Box
                        sx={{
                          display: 'flex',
                          mr: 1,
                          color: (t) => t.palette.text.secondary,
                          '& svg': { width: 18, height: 18 },
                        }}
                      >
                        <Magnify />
                      </Box>
                    ),
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="County"
                value={countyFilter}
                onChange={(e) => setCountyFilter(e.target.value)}
              >
                {counties.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Category"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value={ALL_CATEGORIES}>{ALL_CATEGORIES}</MenuItem>
                {RESOURCE_CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value={ALL_STATUSES}>{ALL_STATUSES}</MenuItem>
                {RESOURCE_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Geographic area"
                value={geoFilter}
                onChange={(e) => setGeoFilter(e.target.value)}
              >
                {geographicAreas.map((g) => (
                  <MenuItem key={g} value={g}>
                    {g}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Box>
        <TableContainer sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell align="center">Name</TableCell>
                <TableCell align="center">Category</TableCell>
                <TableCell align="center">County</TableCell>
                <TableCell align="center">Organization</TableCell>
                <TableCell sx={{ width: 90 }}>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {r.name}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">{r.category}</TableCell>
                  <TableCell align="center">{r.county}</TableCell>
                  <TableCell align="center">{r.organization}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={r.status}
                      size="small"
                      variant="outlined"
                      color={STATUS_COLOR[r.status] ?? 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit resource">
                      <IconButton
                        size="small"
                        aria-label={`Edit ${r.name}`}
                        onClick={() => openEdit(r)}
                        sx={{ '& svg': { width: 18, height: 18 } }}
                      >
                        <Pencil />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete resource">
                      <IconButton
                        size="small"
                        color="error"
                        aria-label={`Delete ${r.name}`}
                        onClick={() => setPendingDelete(r)}
                        sx={{ '& svg': { width: 18, height: 18 } }}
                      >
                        <TrashCan />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: (theme) => theme.spacing(4) }}>
                    <Typography
                      variant="body2"
                      sx={{ color: (theme) => theme.palette.text.secondary }}
                    >
                      No resources match your search.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>

      <ResourceFormDialog
        open={dialogOpen}
        resource={editing}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
      />

      <Dialog open={pendingDelete !== null} onClose={() => setPendingDelete(null)}>
        <DialogTitle>Delete resource</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove “{pendingDelete?.name}” from the resource list? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)}>Cancel</Button>
          <Button variant="contained" color="error" startIcon={<TrashCan />} onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
